import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Building2, LayoutDashboard, Package, Users, DollarSign,
  FileText, Settings, LogOut, Menu, X, ChevronDown, Bell,
  ShoppingCart, Activity, Gift, MessageCircle, Wallet, Store,
  Truck, Map
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";

export const EnterpriseLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('enterprise_user');
    const storedCompany = localStorage.getItem('enterprise_company');

    if (storedUser) setUser(JSON.parse(storedUser));
    if (storedCompany) setCompany(JSON.parse(storedCompany));

    // Check authentication
    const token = localStorage.getItem('enterprise_token');
    if (!token) {
      navigate('/enterprise');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('enterprise_token');
    localStorage.removeItem('enterprise_user');
    localStorage.removeItem('enterprise_company');
    navigate('/enterprise');
  };

  const navItems = [
    {
      path: '/enterprise/dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      roles: ['buyer', 'manager', 'finance', 'admin', 'giftcard_admin', 'super_admin']
    },
    {
      path: '/enterprise/orders',
      icon: Package,
      label: 'Kòmand',
      roles: ['buyer', 'manager', 'finance', 'admin', 'super_admin']
    },
    {
      path: '/enterprise/marketplace/orders',
      icon: Store,
      label: 'Market Order',
      roles: ['super_admin']
    },
    {
      path: '/enterprise/linkamazon',
      icon: ShoppingCart,
      label: 'LinkAmazon',
      roles: ['super_admin']  // Sèlman Super Admin ka jere kòmand LinkAmazon
    },
    {
      path: '/enterprise/sellers',
      icon: Users,
      label: 'Machann',
      roles: ['super_admin']
    },
    {
      path: '/enterprise/dispatch',
      icon: Map,
      label: 'Dispatch Chofè',
      roles: ['super_admin']
    },
    {
      path: '/enterprise/drivers',
      icon: Truck,
      label: 'Jesyon Chofè',
      roles: ['super_admin']
    },
    {
      path: '/enterprise/giftcards',
      icon: Gift,
      label: 'Kat Kado Amazon',
      roles: ['giftcard_admin', 'super_admin']
    },
    {
      path: '/enterprise/giftcards?tab=meru',
      icon: Wallet,
      label: 'Rechaj Meru',
      roles: ['giftcard_admin', 'super_admin']
    },
    {
      path: '/enterprise/users',
      icon: Users,
      label: 'Itilizatè',
      roles: ['manager', 'super_admin']  // Admin pa ka jere itilizatè
    },
    {
      path: '/enterprise/budgets',
      icon: DollarSign,
      label: 'Bidjè',
      roles: ['finance', 'super_admin']  // Admin pa ka jere bidjè
    },
    {
      path: '/enterprise/reports',
      icon: FileText,
      label: 'Rapò',
      roles: ['manager', 'finance', 'giftcard_admin', 'super_admin']  // Admin pa ka wè rapò
    },
    {
      path: '/enterprise/payouts',
      icon: DollarSign,
      label: 'Peman (Payouts)',
      roles: ['finance', 'super_admin']
    },
    {
      path: '/enterprise/withdrawals',
      icon: Wallet,
      label: 'Demand Retrè',
      roles: ['finance', 'super_admin']
    },
    {
      path: '/enterprise/activity',
      icon: Activity,
      label: 'Aktivite',
      roles: ['super_admin']  // Sèlman Super Admin
    },
    {
      path: '/enterprise/whatsapp',
      icon: MessageCircle,
      label: 'WhatsApp',
      roles: ['super_admin']  // Sèlman Super Admin
    },
    {
      path: '/enterprise/settings',
      icon: Settings,
      label: 'Paramèt',
      roles: ['super_admin']  // Sèlman Super Admin
    },
  ];

  const filteredNavItems = navItems.filter(
    item => item.roles.includes(user?.role || 'buyer')
  );

  const isActive = (path) => {
    const currentPath = location.pathname.replace(/\/$/, "");
    const targetBase = path.split('?')[0].replace(/\/$/, "");

    if (path.includes('?')) {
      return (location.pathname + location.search) === path;
    }
    return currentPath === targetBase;
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="enterprise-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#232F3E] h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-white/10"
            data-testid="mobile-sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="BVG Link Amazon"
              className="h-10 w-auto bg-white p-1 rounded"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
        </div>
      </header>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-[#232F3E] transform transition-transform duration-300
          lg:translate-x-0 flex flex-col
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="enterprise-sidebar"
      >
        {/* Logo + Notifications (Desktop) */}
        <div className="h-16 flex-shrink-0 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/enterprise/dashboard" className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="BVG Link Amazon"
              className="h-12 w-auto bg-white p-1 rounded-lg"
            />
            <div className="hidden xl:block">
              <span className="font-bold text-white block leading-tight text-sm">BVGLINK</span>
              <span className="text-[10px] text-[#FF9900] font-bold uppercase tracking-wider">Administration</span>
            </div>
          </Link>
          <div className="hidden lg:block">
            <NotificationBell />
          </div>
        </div>

        {/* Scrollable Nav Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* Company Info */}
          {company && (
            <div className="px-4 py-4 border-b border-white/10">
              <p className="text-white font-medium truncate">{company.name}</p>
              <p className="text-gray-400 text-sm">Code: {company.code}</p>
            </div>
          )}

          {/* Navigation */}
          <nav className="p-4 space-y-2">
            {filteredNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive(item.path)
                    ? 'bg-[#FF9900] text-black font-semibold'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="flex-shrink-0 p-4 border-t border-white/10 bg-[#232F3E]">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-white/10 hover:text-white rounded-lg mb-2"
          >
            <ShoppingCart className="w-5 h-5 text-[#FF9900]" />
            <span className="font-medium px-1">Boutik Kat Kado</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-lg"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Dekonekte</span>
          </button>

          {/* User Info */}
          {user && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-black font-bold ${user.role === 'super_admin' ? 'bg-red-500' : 'bg-[#FF9900]'
                  }`}>
                  {user.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{user.name}</p>
                  <p className={`text-xs uppercase font-bold ${user.role === 'super_admin' ? 'text-red-400' : 'text-gray-400'
                    }`}>
                    {user.role === 'super_admin' ? '⭐ SUPER ADMIN' : user.role?.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
